// D3 Stacked Area Chart for BodyOS data visualizations
// Perfect for showing composition changes over time (sleep stages by age, etc.)
import * as d3 from 'd3'
import {
  colors,
  fonts,
  animationConfig,
  defaultMargins,
  defaultDimensions
} from './config'
import {
  createSvg,
  createXAxis,
  createYAxis,
  createTooltip,
  showTooltipHtml,
  hideTooltip,
  createLegend,
  debounce
} from './utils'

export default class StackedAreaChart {
  constructor(container, options = {}) {
    this.container = typeof container === 'string' ? document.querySelector(container) : container
    this.options = {
      width: defaultDimensions.width,
      height: defaultDimensions.height,
      margins: { ...defaultMargins },
      xLabel: '',
      yLabel: '',
      showLegend: true,
      showValues: false,
      animate: true,
      responsive: true,
      normalized: false, // If true, stack to 100%
      curveType: 'curveMonotoneX', // or 'curveLinear', 'curveBasis'
      ...options
    }

    this.data = []
    this.svg = null
    this.tooltip = null

    if (this.options.responsive) {
      this.handleResize = debounce(() => this.render(), 250)
      window.addEventListener('resize', this.handleResize)
    }
  }

  setData(data) {
    this.data = data
    return this
  }

  render() {
    this.container.innerHTML = ''

    if (!this.data || this.data.length === 0) {
      this.renderEmptyState()
      return this
    }

    const containerWidth = this.container.clientWidth || this.options.width
    const width = Math.min(containerWidth, this.options.width)
    const height = (width / this.options.width) * this.options.height

    const { svg, g, innerWidth, innerHeight } = createSvg(
      this.container,
      width,
      height,
      this.options.margins
    )
    this.svg = svg

    this.tooltip = createTooltip(this.container)

    // Data format: { categories: [...], series: [{ name, values: [...] }] }
    const { categories, series } = this.data

    // Create stack generator
    const keys = series.map((s) => s.name)
    const stackData = categories.map((cat, i) => {
      const point = { category: cat }
      series.forEach((s) => {
        point[s.name] = s.values[i]
      })
      return point
    })

    const stack = d3.stack()
      .keys(keys)
      .order(d3.stackOrderNone)
      .offset(this.options.normalized ? d3.stackOffsetExpand : d3.stackOffsetNone)

    const stackedData = stack(stackData)

    // X scale
    const xScale = d3.scalePoint()
      .domain(categories)
      .range([0, innerWidth])

    // Y scale
    const yMax = this.options.normalized ? 1 : d3.max(stackedData, (layer) => d3.max(layer, (d) => d[1]))
    const yScale = d3.scaleLinear()
      .domain([0, yMax])
      .range([innerHeight, 0])
      .nice()

    // Create axes
    createXAxis(g, xScale, innerHeight, {
      label: this.options.xLabel,
      rotate: categories.length > 10 ? -45 : 0
    })

    createYAxis(g, yScale, {
      label: this.options.yLabel,
      gridLines: true,
      width: innerWidth,
      formatValue: this.options.normalized ? (d) => `${Math.round(d * 100)}%` : null
    })

    // Area generator
    const curve = d3[this.options.curveType] || d3.curveMonotoneX
    const area = d3.area()
      .x((d) => xScale(d.data.category))
      .y0((d) => yScale(d[0]))
      .y1((d) => yScale(d[1]))
      .curve(curve)

    // Draw areas
    const areas = g.selectAll('.stacked-area')
      .data(stackedData)
      .enter()
      .append('path')
      .attr('class', 'stacked-area')
      .attr('d', area)
      .attr('fill', (d, i) => series[i].color || colors.chart[i % colors.chart.length])
      .attr('stroke', colors.neutral[900])
      .attr('stroke-width', 1.5)
      .style('cursor', 'pointer')

    if (this.options.animate) {
      areas
        .attr('opacity', 0)
        .transition()
        .duration(animationConfig.duration)
        .delay((d, i) => i * 100)
        .attr('opacity', 0.85)
    } else {
      areas.attr('opacity', 0.85)
    }

    // Add interactions
    this.addAreaInteractions(areas, series, xScale, yScale, innerHeight)

    // Legend
    if (this.options.showLegend) {
      const legendItems = series.map((s, i) => ({
        label: s.name,
        color: s.color || colors.chart[i % colors.chart.length]
      }))
      createLegend(g, legendItems, {
        x: 0,
        y: -30,
        direction: 'horizontal'
      })
    }

    return this
  }

  addAreaInteractions(areas, series, _xScale, _yScale, _innerHeight) {
    const self = this

    areas
      .on('mouseover', function(event, d) {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 1)

        const seriesIndex = series.findIndex((s) => s.name === d.key)
        const seriesInfo = series[seriesIndex]
        const _color = seriesInfo.color || colors.chart[seriesIndex % colors.chart.length]

        const content = `<strong>${d.key}</strong>`
        showTooltipHtml(self.tooltip, content, event)
      })
      .on('mouseout', function() {
        d3.select(this)
          .transition()
          .duration(200)
          .attr('opacity', 0.85)
        hideTooltip(self.tooltip)
      })
  }

  renderEmptyState() {
    const emptyDiv = document.createElement('div')
    emptyDiv.className = 'd3-empty-state'
    emptyDiv.innerHTML = `
      <div style="
        display: flex;
        align-items: center;
        justify-content: center;
        height: 200px;
        border: 2px dashed ${colors.neutral[300]};
        color: ${colors.textMuted};
        font-family: ${fonts.family};
        font-size: ${fonts.sizes.base}px;
      ">
        NO DATA AVAILABLE
      </div>
    `
    this.container.appendChild(emptyDiv)
  }

  destroy() {
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize)
    }
    if (this.container) {
      this.container.innerHTML = ''
    }
  }
}
